
import { defineComponent } from '@vue/composition-api';
import { mapActions, mapGetters } from 'vuex';
import orderBy from 'lodash/orderBy';
import find from 'lodash/find';
import size from 'lodash/size';
import IllinoisIcons from '../icons.vue';
import {
  checkIsMobileDevice,
  checkIsTabletDevice
} from '../../../../plugins/device-inspector';
import {
  CashoutBet,
  BetOdd,
  LivescoreResultItem
} from '../../../../../types/sport';
import BetItem from '../MyBetsComponents/BetItem.vue';
import CashoutBetDetailModal from '../MyBetsComponents/CashoutBetDetailModal.vue';

// Costante per le etichette statistiche per sport
const STATISTICS_LABELS: Record<string | number, Record<number, string>> = {
  // FOOTBALL/SOCCER
  8412615417: {
    1: 'Corners',
    2: 'Shots On Target',
    3: 'Shots Off Target',
    4: 'Attacks',
    5: 'Dangerous Attacks',
    6: 'Yellow Card',
    7: 'Red Card',
    8: 'Penalties',
    9: 'Goal',
    10: 'Substitutions',
    11: 'Possession',
    12: 'Fouls',
    13: 'Free Kicks',
    14: 'Goal Kicks',
    15: 'Offsides',
    16: 'Blocked Shots',
    17: 'Throw Ins',
    18: 'Woodwork Shots',
    24: 'Own Goal',
    27: 'Score',
    96: 'Goalkeeper Saves',
    102: 'Player Minutes Played',
    148: 'Pass Attempts',
    149: 'Pass Completions',
    1028: 'Player Blocked Shots',
    1036: 'Player Assists',
    2001: 'Player Goals',
    2003: 'Player Tackles',
    2004: 'Player Saves',
    2005: 'Player Shots On Target',
    2006: 'Player Shots Off Target',
    2007: 'Player Fouls',
    2034: 'Assists',
    2154: 'Player Offsides',
    2155: 'Player Yellow Card',
    2173: 'Player Red Card',
    2228: 'Tackles',
    2255: 'Player Total Shots',
    2289: 'Header Goals',
    2344: 'Woodwork Shots'
  },
  // TENNIS
  3467550448: {
    20: 'Aces',
    21: 'Double Faults',
    22: 'Service Points',
    23: 'Break Points',
    27: 'Score',
    34: 'First Serve Wins',
    55: 'Number Of Points',
    63: 'Second Serve Return Points Won',
    65: 'Service Game Won',
    67: 'Break Points Saved',
    68: 'Second Serve Wins',
    70: 'First Serve Return Points Won',
    78: 'Return Games Played',
    2044: 'Service Points Won',
    2045: 'Receiver Points Won',
    2065: 'Max Points In Row',
    2066: 'First Serve Percentage',
    2068: 'Service Errors',
    2090: 'Max Games In Row',
    2094: 'Second Serve Percentage',
    2095: 'Break Points Won Percentage',
    2147: 'Total Games Won',
    2194: 'Successful 1st Serves (Raw)',
    2195: '1st Serve Points Won (%)',
    2196: '2nd Serve Points Won (%)',
    2197: 'Successful 2nd Serves (Raw)',
    2198: '1st Serve Return Points Won (%)',
    2199: '2nd Serve Return Points Won (%)',
    2202: 'Tie Breaks Won (Raw)',
    2205: '1st Serve Attempts (Raw)',
    2206: '2nd Serve Attempts (Raw)',
    2207: '1st Serve Points Played (Raw)',
    2208: '2nd Serve Points Played (Raw)',
    2209: 'Break Points Attempts (Raw)',
    2231: '1st Serve Return Points Played',
    2232: '2nd Serve Return Points Played',
    2299: 'Service Games Played (Raw)'
  },
  // BASKETBALL
  5177137473: {
    12: 'Fouls',
    27: 'Score',
    28: '2 Points',
    30: '3 Points',
    31: 'Timeouts',
    32: 'Free Throws',
    98: 'Player Technical Fouls',
    99: 'Player Blocks Against',
    100: 'Player Defensive Rebounds',
    101: 'Player Offensive Rebounds',
    102: 'Player Minutes Played',
    103: 'Player Field Goals Made',
    104: 'Player Total Rebounds',
    105: 'Player Personal Fouls',
    106: 'Player Free Throws Made',
    107: 'Player Field Goals Attempted',
    108: 'Player 2-Point FG Made',
    109: 'Player Steals',
    110: 'Player Free Throws Attempted',
    111: 'Player 3-Point FG Attempted',
    112: 'Player Turnovers',
    113: 'Player 3-Point FG Made',
    114: 'Player 2-Point FG Attempted',
    1025: 'Player +/- Points',
    1028: 'Player Blocked Shots',
    1033: 'Player Points',
    1036: 'Player Assists',
    2007: 'Player Fouls',
    2029: 'Rebounds',
    2030: 'Defensive Rebounds',
    2031: 'Offensive Rebounds',
    2034: 'Assists',
    2035: 'Steals',
    2214: 'Player Free Throws Made (%)',
    2222: 'Player 2 Pointers Made (%)',
    2223: 'Player 3 Pointers Made (%)',
    2224: 'Player FG Made (%)'
  },
  // VOLLEYBALL
  2908586663: {
    16: 'Blocked Shots',
    20: 'Aces',
    27: 'Score'
  },
  // BASEBALL
  4105083646: {
    27: 'Score',
    33: 'Hits',
    93: 'Runs',
    1009: 'Pitcher Base on Balls',
    1010: 'Pitcher Strikeouts',
    1022: 'Batter Strikeouts',
    1023: 'Batter Left on Base',
    2080: 'Doubles',
    2081: 'Triples',
    2082: 'Home Runs',
    2083: 'Strike Outs',
    2096: 'Errors',
    2097: 'Stolen Bases'
  }
};

interface CashoutBetV2 extends CashoutBet {
  cashoutAvailable?: boolean;
  cashoutValue?: number | null;
  cashoutError?: string | any[] | null;
  cashoutTemporarilyUnavailable?: boolean;
  isCheckingCashout?: boolean;
  isProcessingCashout?: boolean;
  cashoutProcessed?: boolean;
  hasOddOutOfRange?: boolean;
  hasEventsOutOfRange?: boolean;
}

interface ModalOptions {
  title?: string;
  text?: string;
  html?: string;
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  closeBetDetail?: boolean;
  autoClose?: number;
  openBetDetailModal?: boolean;
}

interface ModalResult {
  isConfirmed: boolean;
  dismiss?: string;
  autoclosed?: boolean;
}

export default defineComponent({
  name: 'IllinoisMyBetsCashout',
  components: {
    BetItem,
    CashoutBetDetailModal,
    IllinoisIcons
  },
  data() {
    return {
      dialog: false,
      overlay_dialog_betlist_detail: false,
      betToShow: {} as CashoutBetV2,
      loading: true,
      overlay: false,
      skeleton_loader: false,

      // Paginazione
      currentPage: 1,
      numPerPage: 6,
      apiLoading: false,

      // Polling
      cashoutIntervalId: null as number | null,

      // Modali custom
      showCashoutModal: false,
      modalTitle: '',
      modalMessage: '',
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      modalPromise: null as ((value: ModalResult) => void) | null,
      scrollPosition: 0
    };
  },

  computed: {
    ...mapGetters({
      getLanguage: 'settings/getLanguage',
      getAuthenticated: 'user/getAuthenticated',
      getPlatformSettings: 'settings/getPlatformSettings',
      getDetailMyBets: 'user/getDetailMyBets',
      getModalIdle: 'user/getModalIdle',
      getOddsType: 'odds/getOddsType',
      getSettings: 'cart/getSettings',
      getCashoutApiPollIntervalSec: 'settings/getCashoutApiPollIntervalSec',
      getMyBetsCashout: 'user/getMyBetsInProgressCashout',
      getCashoutPagination: 'user/getMyBetsPaginationCashout'
    })
  },

  watch: {
    overlay(val: boolean) {
      if (val) {
        setTimeout(() => {
          this.overlay = false;
        }, 3000);
      }
    }
  },

  methods: {
    ...mapActions({
      resetCart: 'cart/resetCart',
      setOddsInCart: 'cart/setOddsInCart',
      refreshCart: 'cart/refreshCart',
      fetchEventResultAndTimeDetail: 'odds/fetchEventResultAndTimeDetail',
      setCreatedAtCart: 'cart/setCreatedAtCart',
      setDetailMyBet: 'user/setDetailMyBet',
      calculateCashout: 'cart/calculateCashout',
      defineCashout: 'cart/defineCashout',
      fetchMyBetsInProgressCashoutPaginated:
        'user/fetchMyBetsInProgressCashoutPaginated'
    }),

    adaptBetForBetItem(bet: CashoutBetV2): any {
      // Crea una copia del bet per non modificare l'originale
      const adaptedBet: any = { ...bet };

      // Mappa code a betCode se mancante
      if (!adaptedBet.betCode && adaptedBet.code) {
        adaptedBet.betCode = adaptedBet.code;
      }

      // Assicura che _multiple_detail esista con valori di default se opzionale
      if (!adaptedBet._multiple_detail) {
        adaptedBet._multiple_detail = {
          winning_total: 0
        };
      }

      // Assicura che _integral_detail esista con valori di default se opzionale
      if (!adaptedBet._integral_detail) {
        adaptedBet._integral_detail = {
          winning_total_max: 0
        };
      }

      // Assicura che _system_detail esista con valori di default se opzionale
      if (!adaptedBet._system_detail) {
        adaptedBet._system_detail = {
          winning_all_total: 0
        };
      }

      return adaptedBet;
    },

    t(key: string): string {
      return this.$t(key) as string;
    },

    checkIsMobile(): boolean {
      return checkIsMobileDevice();
    },

    checkIsTablet(): boolean {
      return checkIsTabletDevice();
    },

    formatDate(dateStr: string): string {
      return new Date(dateStr).toLocaleString();
    },

    formatDateShort(dateStr: string): string {
      const date = new Date(dateStr);
      return (
        date.toLocaleDateString('en-GB', { month: 'numeric', day: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    },

    formatNumber(value: number | null | undefined): string {
      return parseFloat(String(value || 0)).toFixed(2);
    },

    // Paginazione
    async goToPage(page: number): Promise<void> {
      if (page === this.currentPage) return;
      this.currentPage = page;
      this.loading = true;
      this.$emit('loading-change', true);
      await this.fetchMyBetsInProgressCashoutPaginated({
        all: false,
        betTypes: ['single', 'multiple'],
        page: this.currentPage,
        numPerPage: this.numPerPage
      });
      await this.checkCashoutForPendingBets();
      this.loading = false;
      this.$emit('loading-change', false);
      this.emitPaginationState();
    },
    goToNextPage(): void {
      const totalPages = this.getCashoutPagination?.totalPages || 1;
      if (this.currentPage < totalPages) {
        this.goToPage(this.currentPage + 1);
      }
    },
    goToPrevPage(): void {
      if (this.currentPage > 1) {
        this.goToPage(this.currentPage - 1);
      }
    },
    emitPaginationState(): void {
      const totalPages = this.getCashoutPagination?.totalPages || 1;
      this.$emit('pagination-change', {
        currentPage: this.currentPage,
        totalPages: totalPages,
        canGoNext: this.currentPage < totalPages,
        canGoPrev: this.currentPage > 1,
        hasBets: this.getMyBetsCashout && this.getMyBetsCashout.length > 0
      });
    },

    // Modali custom
    showModal(options: ModalOptions): Promise<ModalResult> {
      this.dialog = false;

      // Blocca lo scroll
      this.scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';

      this.modalTitle = options.title || '';
      this.modalMessage = options.html || options.text || '';
      this.showConfirmButton = options.showConfirmButton !== false;
      this.showCancelButton = options.showCancelButton !== false;
      this.confirmButtonText =
        this.t('cashout.confirmButton') ||
        options.confirmButtonText ||
        'Confirm';
      this.cancelButtonText =
        this.t('cashout.cancelButton') || options.cancelButtonText || 'Cancel';
      this.showCashoutModal = true;

      // Gestione autoClose
      if (options.autoClose) {
        setTimeout(() => {
          this.closeModal({});
        }, options.autoClose);
      }

      return new Promise(resolve => {
        this.modalPromise = resolve;

        if (
          !this.showConfirmButton &&
          !this.showCancelButton &&
          options.autoClose
        ) {
          setTimeout(() => {
            if (this.modalPromise) {
              this.modalPromise({ isConfirmed: true, autoclosed: true });
              this.modalPromise = null;
            }
          }, options.autoClose);
        }
      });
    },

    closeModal(options: ModalOptions): void {
      this.showCashoutModal = false;

      if (options.openBetDetailModal === true) {
        this.dialog = true;
      }

      // Ripristina lo scroll
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      window.scrollTo(0, this.scrollPosition);

      if (this.modalPromise) {
        this.modalPromise({ isConfirmed: false, dismiss: 'overlay' });
        this.modalPromise = null;
      }
    },

    confirmModalAction(): void {
      if (this.modalPromise) {
        this.modalPromise({ isConfirmed: true });
        this.modalPromise = null;
      }
      this.closeModal({});
    },

    cancelModalAction(): void {
      if (this.modalPromise) {
        this.modalPromise({ isConfirmed: false, dismiss: 'cancel' });
        this.modalPromise = null;
      }
      this.closeModal({ openBetDetailModal: true });
    },

    // Cashout
    async processCashout(bet: CashoutBetV2): Promise<void> {
      // 1. Prima conferma
      this.$set(bet, 'isProcessingCashout', true);
      const confirm1 = await this.showModal({
        title: this.t('cashout.confirmTitle'),
        text: this.t('cashout.confirmText'),
        confirmButtonText: this.t('cashout.yesContinue'),
        cancelButtonText: this.t('cashout.cancelButton'),
        showCancelButton: true,
        showConfirmButton: true,
        closeBetDetail: true
      });
      if (!confirm1.isConfirmed) {
        this.$set(bet, 'isProcessingCashout', false);
        return;
      }
      await this.$nextTick();

      // 2. Recupera cashout aggiornato dal server
      let updatedCashoutValue = bet.cashoutValue;
      let updatedCashoutError: string | null = null;
      try {
        const response = await this.calculateCashout({
          code: bet.code,
          useMinuteEvaluation: true
        });

        if (
          response &&
          response.data &&
          response.data.result === true &&
          response.data.cashoutData
        ) {
          updatedCashoutValue = response.data.cashoutData.cashoutToConsider3;
          this.$set(bet, 'cashoutValue', updatedCashoutValue);
          this.$set(bet, 'cashoutAvailable', true);
          this.$set(bet, 'cashoutError', null);
          this.$set(bet, 'cashoutTemporarilyUnavailable', false);
        } else {
          const errors =
            response.data && response.data.errors ? response.data.errors : [];
          const isDisabled =
            Array.isArray(errors) &&
            errors.some(
              (e: any) => (e.code || '').toUpperCase() === 'CASHOUT_DISABLED'
            );
          if (isDisabled) {
            updatedCashoutError = this.t('cashout.notAvailable');
            this.$set(bet, 'cashoutAvailable', false);
            this.$set(bet, 'cashoutTemporarilyUnavailable', false);
            this.$set(bet, 'cashoutError', errors);
            this.$set(bet, 'cashoutValue', null);
          } else {
            updatedCashoutError = this.t('cashout.temporarilyUnavailable');
            this.$set(bet, 'cashoutAvailable', false);
            this.$set(bet, 'cashoutTemporarilyUnavailable', true);
            this.$set(
              bet,
              'cashoutError',
              errors.length > 0 ? errors : updatedCashoutError
            );
            this.$set(bet, 'cashoutValue', null);
          }
        }
      } catch (err: any) {
        updatedCashoutError = err.message || this.t('cashout.errorText');
        this.$set(bet, 'cashoutAvailable', false);
        this.$set(bet, 'cashoutTemporarilyUnavailable', false);
        this.$set(bet, 'cashoutError', updatedCashoutError);
        this.$set(bet, 'cashoutValue', null);
      }
      await this.$nextTick();

      // 3. Se cashout non disponibile, mostra modale errore
      if (!bet.cashoutAvailable || !updatedCashoutValue) {
        await this.showModal({
          title: this.t('cashout.temporarilyUnavailableTitle'),
          text: updatedCashoutError || this.t('cashout.temporarilyUnavailable'),
          confirmButtonText: this.t('generic.close'),
          showCancelButton: false,
          showConfirmButton: true
        });
        this.$set(bet, 'isCheckingCashout', false);
        this.$set(bet, 'isProcessingCashout', false);
        return;
      }

      // 4. Seconda conferma con cashout aggiornato
      const formattedAmount =
        this.getSettings.currencySymbol +
        this.formatNumber(updatedCashoutValue);
      const confirm2 = await this.showModal({
        title: this.t('cashout.finalConfirmTitle'),
        html: this.t('cashout.finalConfirmText', {
          amount: '<strong>' + formattedAmount + '</strong>'
        }),
        confirmButtonText: this.t('cashout.confirmButton'),
        cancelButtonText: this.t('cashout.cancelButton'),
        showCancelButton: true,
        showConfirmButton: true
      });
      if (!confirm2.isConfirmed) {
        this.$set(bet, 'isProcessingCashout', false);
        return;
      }
      await this.$nextTick();

      // 5. Chiamata API cashout
      try {
        const response = await this.defineCashout({ code: bet.code });
        const apiResult = response && response.data ? response.data : null;
        if (
          apiResult &&
          apiResult.result === true &&
          apiResult.cashoutConfirmed === true
        ) {
          // Successo
          await this.showModal({
            title: this.t('cashout.successTitle'),
            html: this.t('cashout.successText', {
              amount: '<strong>' + formattedAmount + '</strong>'
            }),
            showCancelButton: false,
            showConfirmButton: false,
            autoClose: 2000
          });
          this.$set(bet, 'cashoutAvailable', false);
          this.$set(bet, 'cashoutProcessed', true);
          this.$set(bet, 'isCheckingCashout', false);
          this.$set(bet, 'isProcessingCashout', false);
          this.dialog = false;
          await this.initialize();
        } else {
          let errorMsg = this.t('cashout.errorText');
          let errors: any[] = [];
          if (
            apiResult &&
            apiResult.paymentResult &&
            apiResult.paymentResult.errors
          ) {
            errors = apiResult.paymentResult.errors;
          } else if (
            apiResult &&
            apiResult.errors &&
            apiResult.errors.length > 0
          ) {
            errors = apiResult.errors;
          }

          // FROZEN_EXPIRED
          const isFrozenExpired =
            Array.isArray(errors) &&
            errors.some(
              (e: any) => (e.code || '').toUpperCase() === 'FROZEN_EXPIRED'
            );
          if (isFrozenExpired) {
            await this.showModal({
              title: this.t('error'),
              html: `
                <div class="flex flex-col items-center">
                  <span class="text-red-500 text-3xl mb-4">⏳</span>
                  <span class="text-yellow-500 font-bold text-lg">
                    ${
                      this.t('cashout.frozenExpired') ||
                      'In the meantime, the odds changed! Please try again quickly.'
                    }
                  </span>
                  <span class="text-red-500 text-sm mt-2">
                    ${
                      this.t('cashout.frozenExpiredHint') ||
                      'Cashout must be confirmed quickly to guarantee the current offer.'
                    }
                  </span>
                </div>
              `,
              confirmButtonText: this.t('generic.close'),
              showCancelButton: false,
              showConfirmButton: true
            });
            this.$set(bet, 'cashoutAvailable', false);
            this.$set(bet, 'cashoutTemporarilyUnavailable', false);
            this.$set(bet, 'cashoutError', errors);
            this.$set(bet, 'cashoutValue', null);
            this.$set(bet, 'isCheckingCashout', false);
            this.$set(bet, 'isProcessingCashout', false);
            return;
          }

          const isDisabled =
            Array.isArray(errors) &&
            errors.some(
              (e: any) => (e.code || '').toUpperCase() === 'CASHOUT_DISABLED'
            );
          if (isDisabled) {
            this.$set(bet, 'cashoutAvailable', false);
            this.$set(bet, 'cashoutTemporarilyUnavailable', false);
            this.$set(bet, 'cashoutError', errors);
            this.$set(bet, 'cashoutValue', null);
            errorMsg = this.t('cashout.notAvailable');
          } else {
            this.$set(bet, 'cashoutAvailable', false);
            this.$set(bet, 'cashoutTemporarilyUnavailable', true);
            this.$set(
              bet,
              'cashoutError',
              errors.length > 0 ? errors : errorMsg
            );
            this.$set(bet, 'cashoutValue', null);
          }
          await this.showModal({
            title: this.t('cashout.failedTitle') || this.t('error'),
            text: errorMsg,
            confirmButtonText: this.t('generic.close'),
            showCancelButton: false,
            showConfirmButton: true
          });
          this.$set(bet, 'isCheckingCashout', false);
          this.$set(bet, 'isProcessingCashout', false);
        }
      } catch (error) {
        await this.showModal({
          title: this.t('error'),
          text: this.t('cashout.errorText'),
          confirmButtonText: this.t('generic.close'),
          showCancelButton: false,
          showConfirmButton: true
        });
        this.$set(bet, 'cashoutAvailable', false);
        this.$set(bet, 'cashoutTemporarilyUnavailable', false);
        this.$set(bet, 'isCheckingCashout', false);
        this.$set(bet, 'isProcessingCashout', false);
      } finally {
        this.$set(bet, 'isProcessingCashout', false);
      }
    },

    async checkCashoutForPendingBets(): Promise<void> {
      const pendingBets = this.getMyBetsCashout.filter(
        (bet: CashoutBetV2) =>
          bet.result === 'pending' &&
          bet.cashoutValue == null &&
          !bet.isCheckingCashout &&
          !bet.hasOddOutOfRange &&
          !bet.hasEventsOutOfRange
      );

      if (pendingBets.length === 0) return;

      const cashoutPromises = pendingBets.map(async (bet: CashoutBetV2) => {
        const betIndex = this.getMyBetsCashout.findIndex(
          (b: CashoutBetV2) => b.code === bet.code
        );
        if (betIndex === -1) return;
        this.$set(this.getMyBetsCashout[betIndex], 'isCheckingCashout', true);

        try {
          const response = await this.calculateCashout({
            code: bet.code,
            useMinuteEvaluation: true
          });
          const errors =
            response.data && response.data.errors ? response.data.errors : [];

          // ODD_OUT_OF_RANGE
          const hasOddOutOfRange = errors.some(
            (e: any) => (e.code || '').toUpperCase() === 'ODD_OUT_OF_RANGE'
          );
          this.$set(
            this.getMyBetsCashout[betIndex],
            'hasOddOutOfRange',
            hasOddOutOfRange
          );

          // MIN/MAX_EVENTS_NOT_REACHED
          const hasEventsOutOfRange = errors.some((e: any) => {
            const code = (e.code || '').toUpperCase();
            return (
              code === 'MIN_EVENTS_NOT_REACHED' ||
              code === 'MAX_EVENTS_NOT_REACHED'
            );
          });
          this.$set(
            this.getMyBetsCashout[betIndex],
            'hasEventsOutOfRange',
            hasEventsOutOfRange
          );

          if (hasOddOutOfRange || hasEventsOutOfRange) {
            this.$set(
              this.getMyBetsCashout[betIndex],
              'cashoutAvailable',
              false
            );
            this.$set(
              this.getMyBetsCashout[betIndex],
              'cashoutTemporarilyUnavailable',
              false
            );
            this.$set(this.getMyBetsCashout[betIndex], 'cashoutError', errors);
            this.$set(this.getMyBetsCashout[betIndex], 'cashoutValue', null);
            return;
          }

          if (
            response.data &&
            response.data.result === true &&
            response.data.cashoutData
          ) {
            this.$set(
              this.getMyBetsCashout[betIndex],
              'cashoutValue',
              response.data.cashoutData.cashoutToConsider3
            );
            this.$set(
              this.getMyBetsCashout[betIndex],
              'cashoutAvailable',
              true
            );
            this.$set(
              this.getMyBetsCashout[betIndex],
              'cashoutTemporarilyUnavailable',
              false
            );
            this.$set(this.getMyBetsCashout[betIndex], 'cashoutError', null);
          } else {
            const isDisabled =
              Array.isArray(errors) &&
              errors.some(
                (e: any) => (e.code || '').toUpperCase() === 'CASHOUT_DISABLED'
              );
            if (isDisabled) {
              this.$set(
                this.getMyBetsCashout[betIndex],
                'cashoutAvailable',
                false
              );
              this.$set(
                this.getMyBetsCashout[betIndex],
                'cashoutTemporarilyUnavailable',
                false
              );
              this.$set(this.getMyBetsCashout[betIndex], 'cashoutValue', null);
              this.$set(
                this.getMyBetsCashout[betIndex],
                'cashoutError',
                errors
              );
            } else {
              this.$set(
                this.getMyBetsCashout[betIndex],
                'cashoutAvailable',
                false
              );
              this.$set(
                this.getMyBetsCashout[betIndex],
                'cashoutTemporarilyUnavailable',
                true
              );
              this.$set(this.getMyBetsCashout[betIndex], 'cashoutValue', null);
              this.$set(
                this.getMyBetsCashout[betIndex],
                'cashoutError',
                errors.length > 0 ? errors : 'Cashout not available'
              );
            }
          }

          // Aggiorna betToShow se è quella visualizzata
          if (this.betToShow && this.betToShow.code === bet.code) {
            this.betToShow = this.getMyBetsCashout[betIndex];
          }
        } catch (error: any) {
          this.$set(this.getMyBetsCashout[betIndex], 'cashoutAvailable', false);
          this.$set(
            this.getMyBetsCashout[betIndex],
            'cashoutTemporarilyUnavailable',
            false
          );
          this.$set(
            this.getMyBetsCashout[betIndex],
            'cashoutError',
            error.message || 'Cashout not available'
          );
          this.$set(this.getMyBetsCashout[betIndex], 'cashoutValue', null);
        } finally {
          this.$set(
            this.getMyBetsCashout[betIndex],
            'isCheckingCashout',
            false
          );
        }
      });

      await Promise.allSettled(cashoutPromises);
    },

    // Statistiche live
    getOddCurrentScore(odd: BetOdd): string {
      if (
        (odd as any).result_actual &&
        Array.isArray((odd as any).result_actual)
      ) {
        const home = (odd as any).result_actual.find(
          (r: any) => r.Position == '1'
        );
        const away = (odd as any).result_actual.find(
          (r: any) => r.Position == '2'
        );
        if (home || away) {
          return (home ? home.Value : '-') + ' - ' + (away ? away.Value : '-');
        }
      }
      if (
        (odd as any).livescore &&
        (odd as any).livescore.Scoreboard &&
        Array.isArray((odd as any).livescore.Scoreboard.Results)
      ) {
        const home = (odd as any).livescore.Scoreboard.Results.find(
          (r: any) => r.Position == '1'
        );
        const away = (odd as any).livescore.Scoreboard.Results.find(
          (r: any) => r.Position == '2'
        );
        if (home || away) {
          return (home ? home.Value : '-') + ' - ' + (away ? away.Value : '-');
        }
      }
      return '';
    },

    getLivescoreMinute(odd: BetOdd): string | number {
      if (
        (odd as any).livescore &&
        (odd as any).livescore.Scoreboard &&
        typeof (odd as any).livescore.Scoreboard.Time !== 'undefined'
      ) {
        const timeSec = parseInt((odd as any).livescore.Scoreboard.Time, 10);
        if (!isNaN(timeSec) && timeSec >= 0) {
          return Math.floor(timeSec / 60);
        }
        return '';
      }
      if (
        typeof (odd as any).time_actual !== 'undefined' &&
        (odd as any).time_actual !== null &&
        !isNaN(Number((odd as any).time_actual))
      ) {
        const minute = Math.floor(Number((odd as any).time_actual));
        if (minute >= 0) {
          return minute;
        }
        return '';
      }
      return '';
    },

    isMatchInProgress(odd: BetOdd): boolean {
      if (
        odd &&
        ((odd as any).status_event == 2 || (odd as any).status_event === '2')
      ) {
        return true;
      }
      const status =
        this.betToShow &&
        (this.betToShow as any).sport_event_status &&
        (this.betToShow as any).sport_event_status._attributes &&
        (this.betToShow as any).sport_event_status._attributes.status;
      return status == 2 || status === '2';
    },

    getStatLabel(label: string): string {
      const key = `statistics.${label}`;
      const translation = this.$t(key) as string;
      return translation !== key ? translation : label;
    },

    getStatTypeLabel(type: number, odd: BetOdd): string {
      const sportId =
        (odd && (odd as any).sport && (odd as any).sport.id) ||
        (odd && (odd as any).sport_id) ||
        (this.betToShow && (this.betToShow as any).sport_id);
      const mappa = STATISTICS_LABELS[sportId];
      if (!mappa) return 'Stat ' + type;
      return mappa[type] || 'Stat ' + type;
    },

    getStatHomeAwayDisplay(stat: any): string {
      if (!stat || !stat.Results || !Array.isArray(stat.Results)) return '';
      const home = stat.Results.find((r: any) => r.Position == '1');
      const away = stat.Results.find((r: any) => r.Position == '2');
      return (home ? home.Value : '-') + ' - ' + (away ? away.Value : '-');
    },

    statGridRows(odd: BetOdd): any[][] {
      const stats =
        (odd as any).livescore && (odd as any).livescore.Statistics
          ? (odd as any).livescore.Statistics
          : [];
      const maxCols = 6;
      const cells: any[][] = [];
      let row: any[] = [];
      for (let i = 0; i < stats.length; i++) {
        row.push(stats[i]);
        if (row.length === maxCols) {
          cells.push(row);
          row = [];
        }
      }
      if (row.length > 0) {
        while (row.length < maxCols) row.push({});
        cells.push(row);
      }
      return cells;
    },

    getIncHeaders(period: any): any[] {
      const maxCols = 6;
      const typesSeen: Record<string, boolean> = {};
      const cells: any[] = [];
      if (!(period && period.Incidents && period.Incidents.length)) return [];
      for (
        let i = 0;
        i < period.Incidents.length && cells.length < maxCols;
        i++
      ) {
        const type = period.Incidents[i].IncidentType;
        if (!typesSeen[type]) {
          typesSeen[type] = true;
          cells.push({
            type,
            label: this.getStatTypeLabel(
              type,
              this.betToShow.odds && this.betToShow.odds[0]
                ? this.betToShow.odds[0]
                : (this.betToShow as any)
            )
          });
        }
      }
      while (cells.length < maxCols) cells.push({ label: '', type: '' });
      return cells;
    },

    getIncRows(period: any): any[][] {
      const maxCols = 6;
      if (!(period && period.Incidents && period.Incidents.length)) return [];
      const rows: any[][] = [];
      let row: any[] = [];
      for (let i = 0; i < period.Incidents.length; i++) {
        const inc = period.Incidents[i];
        row.push({
          key: 'inc-' + i + '-' + inc.IncidentType,
          label: this.getStatTypeLabel(
            inc.IncidentType,
            this.betToShow.odds && this.betToShow.odds[0]
              ? this.betToShow.odds[0]
              : (this.betToShow as any)
          ),
          value: inc.Results && inc.Results[0] ? inc.Results[0].Value : '',
          seconds: inc.Seconds > 0 ? inc.Seconds + 's' : ''
        });
        if (row.length === maxCols) {
          rows.push(row);
          row = [];
        }
      }
      while (row.length > 0 && row.length < maxCols) {
        row.push({
          key: 'empty-' + row.length,
          label: '',
          value: '',
          seconds: ''
        });
      }
      if (row.length > 0) rows.push(row);
      return rows;
    },

    // Altre funzioni
    printBet(code: string): void {
      window.open(
        this.getPlatformSettings.print_endpoint +
          code +
          '&type_odd=' +
          this.getOddsType,
        '_blank',
        'height=600,width=350'
      );
    },

    async openDetail(bet: CashoutBetV2): Promise<void> {
      this.overlay = true;
      const sleep = (m: number) => new Promise(r => setTimeout(r, m));
      await sleep(100);
      document.body.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.parent.postMessage({ type: 'scrollTop' }, '*');

      this.betToShow = bet;
      const odds = this.betToShow.odds || [];

      try {
        // Cashout promise
        const cashoutPromise = this.calculateCashout({
          code: this.betToShow.code,
          useMinuteEvaluation: true
        }).catch((error: Error) => error);

        // Livescore per ogni odd
        const liveOddsPromises = odds.map(async (odd: BetOdd) => {
          try {
            const data = await this.getLiveDate(
              (odd as any).event_source,
              (odd as any).ext_id
            );
            if (data && data.livescore) {
              this.$set(odd, 'livescore', data.livescore);
            }
            if (data && data.outcomeInfo) {
              this.$set(odd, 'outcomeInfo', data.outcomeInfo);
              this.$set(odd, 'liveOdd', data.outcomeInfo.odds);
            }
          } catch (e) {
            console.warn('Errore ottenendo livescore per odd', odd, e);
          }
        });
        await Promise.all(liveOddsPromises);

        // Gestione cashout
        const cashoutResp = await cashoutPromise;
        if (
          (typeof cashoutResp === 'object' && cashoutResp instanceof Error) ||
          !cashoutResp ||
          !(cashoutResp as any).data
        ) {
          this.$set(this.betToShow, 'cashoutAvailable', false);
          this.$set(this.betToShow, 'cashoutTemporarilyUnavailable', false);
          this.$set(
            this.betToShow,
            'cashoutError',
            this.t('cashout.errorText')
          );
          this.$set(this.betToShow, 'cashoutValue', null);
        } else if (
          (cashoutResp as any).data.result === true &&
          (cashoutResp as any).data.cashoutData
        ) {
          const updatedValue = (cashoutResp as any).data.cashoutData
            .cashoutToConsider3;
          this.$set(this.betToShow, 'cashoutValue', updatedValue);
          this.$set(this.betToShow, 'cashoutAvailable', true);
          this.$set(this.betToShow, 'cashoutTemporarilyUnavailable', false);
          this.$set(this.betToShow, 'cashoutError', null);
        } else {
          const errors =
            (cashoutResp as any).data && (cashoutResp as any).data.errors
              ? (cashoutResp as any).data.errors
              : [];
          const isDisabled =
            Array.isArray(errors) &&
            errors.some(
              (e: any) => (e.code || '').toUpperCase() === 'CASHOUT_DISABLED'
            );
          if (isDisabled) {
            this.$set(this.betToShow, 'cashoutAvailable', false);
            this.$set(this.betToShow, 'cashoutTemporarilyUnavailable', false);
            this.$set(this.betToShow, 'cashoutError', errors);
            this.$set(this.betToShow, 'cashoutValue', null);
          } else {
            this.$set(this.betToShow, 'cashoutAvailable', false);
            this.$set(this.betToShow, 'cashoutTemporarilyUnavailable', true);
            this.$set(
              this.betToShow,
              'cashoutError',
              errors.length > 0
                ? errors
                : this.t('cashout.temporarilyUnavailable')
            );
            this.$set(this.betToShow, 'cashoutValue', null);
          }
        }
      } catch (error) {
        this.$set(this.betToShow, 'cashoutAvailable', false);
        this.$set(this.betToShow, 'cashoutTemporarilyUnavailable', false);
        this.$set(this.betToShow, 'cashoutError', this.t('cashout.errorText'));
        this.$set(this.betToShow, 'cashoutValue', null);
      }

      this.setDetailMyBet(this.betToShow);
      this.dialog = true;
      this.overlay = false;
    },

    getWinning(bet: CashoutBet): string {
      switch (bet.type) {
        case 'single':
        case 'multiple':
          return (
            bet.currencySymbol +
            ' ' +
            parseFloat(
              String(bet._multiple_detail?.winning_total || 0)
            ).toFixed(2)
          );
        case 'integral':
          return (
            bet.currencySymbol +
            ' ' +
            parseFloat(
              String(bet._integral_detail?.winning_total_max || 0)
            ).toFixed(2)
          );
        case 'system':
          return (
            bet.currencySymbol +
            ' ' +
            parseFloat(
              String(bet._system_detail?.winning_all_total || 0)
            ).toFixed(2)
          );
        default:
          return bet.currencySymbol + ' 0.00';
      }
    },

    sortby(array: any[]): any[] {
      if (array.length > 0) {
        return orderBy(array, ['source'], ['asc']);
      }
      return [];
    },

    getSpecifiers(spec: string): string {
      let result = '';
      if (spec && spec.includes('#')) {
        const split = spec.split('#');
        if (split[1].includes(':')) {
          result = '(' + split[1] + ')';
        } else if (split[1].includes('(') && split[1].includes('.')) {
          result = ' [' + split[1] + '] ';
        } else if (split[1] !== '') {
          result = split[1];
        }
        if (size(split) > 2) {
          return result + ' ' + split[2];
        }
        return result;
      }
      return result;
    },

    getResult(result: LivescoreResultItem[], team: string): number {
      if (team === 'home') {
        const point = find(result, { Position: '1' });
        return point ? +point.Value : 0;
      } else if (team === 'away') {
        const point = find(result, { Position: '2' });
        return point ? +point.Value : 0;
      }
      return 0;
    },

    async rebet(odds: BetOdd[]): Promise<void> {
      const now = new Date();
      this.dialog = false;
      await this.resetCart();
      await this.setOddsInCart(odds);
      await this.setCreatedAtCart(now.getTime());
      await this.refreshCart();
      await this.$root.$emit('sectionCart');
    },

    async getLiveDate(
      eventSource: string,
      outcomeExternalId?: string
    ): Promise<any> {
      const data = await this.fetchEventResultAndTimeDetail({
        source: eventSource,
        outcomeExternalId: outcomeExternalId
      });
      return data;
    },

    // Inizializzazione e polling
    async initialize(): Promise<void> {
      this.currentPage = 1;
      this.$emit('loading-change', true);
      await this.reloadBets();
      this.emitPaginationState();
    },

    setupCashoutPolling(): void {
      this.clearCashoutPolling();
      const intervalMs =
        Math.max(Number(this.getCashoutApiPollIntervalSec), 60) * 1000;
      this.cashoutIntervalId = window.setInterval(() => {
        this.reloadBets();
      }, intervalMs);
    },

    clearCashoutPolling(): void {
      if (this.cashoutIntervalId) {
        clearInterval(this.cashoutIntervalId);
        this.cashoutIntervalId = null;
      }
    },

    async reloadBets(): Promise<void> {
      if (this.apiLoading) return;
      this.apiLoading = true;
      this.loading = true;
      this.$emit('loading-change', true);
      await this.fetchMyBetsInProgressCashoutPaginated({
        all: false,
        betTypes: ['single', 'multiple'],
        page: this.currentPage,
        numPerPage: this.numPerPage
      });
      await this.checkCashoutForPendingBets();
      this.loading = false;
      this.apiLoading = false;
      this.$emit('loading-change', false);
      this.emitPaginationState();
    }
  },

  async mounted() {
    await this.initialize();
    this.setupCashoutPolling();
  },

  beforeDestroy() {
    this.clearCashoutPolling();
  }
});
